import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { GastosService } from '../../../../auxiliares/servicios/http/gastos.service';
import { FacturacionClientesService } from '../../../../auxiliares/servicios/http/facturacion-clientes.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';

@Component({
  selector: 'app-crear-resumen',
  templateUrl: './crear-resumen.component.html',
  styleUrl: './crear-resumen.component.scss',
  standalone: false,
})
export class CrearResumenComponent implements OnInit {
  public dialogRef =
    inject<MatDialogRef<CrearResumenComponent>>(MatDialogRef);
  private gastosService = inject(GastosService);
  private facturacionService = inject(FacturacionClientesService);
  private helper = inject(HelperService);

  public form?: FormGroup;
  public loading = false;
  /** Clientes con FacturacionCliente cargada (no bonificada). */
  public clientesFacturables: { idCliente: string; nombre: string }[] = [];
  public meses = [
    { valor: 1, nombre: 'Enero' },
    { valor: 2, nombre: 'Febrero' },
    { valor: 3, nombre: 'Marzo' },
    { valor: 4, nombre: 'Abril' },
    { valor: 5, nombre: 'Mayo' },
    { valor: 6, nombre: 'Junio' },
    { valor: 7, nombre: 'Julio' },
    { valor: 8, nombre: 'Agosto' },
    { valor: 9, nombre: 'Septiembre' },
    { valor: 10, nombre: 'Octubre' },
    { valor: 11, nombre: 'Noviembre' },
    { valor: 12, nombre: 'Diciembre' },
  ];
  public anios: number[] = [];

  async ngOnInit(): Promise<void> {
    // Por defecto: mes anterior.
    const ahora = new Date();
    const mesAnterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
    const anioActual = ahora.getFullYear();
    // Sin años futuros.
    this.anios = [anioActual, anioActual - 1, anioActual - 2];
    this.form = new FormGroup({
      idCliente: new FormControl<string | null>(null, Validators.required),
      mes: new FormControl(mesAnterior.getMonth() + 1, Validators.required),
      anio: new FormControl(mesAnterior.getFullYear(), Validators.required),
    });
    await this.cargarClientesFacturables();
  }

  /** Meses seleccionables: excluye el mes actual y los futuros del año elegido. */
  public get mesesDisponibles(): { valor: number; nombre: string }[] {
    const anio = this.form?.get('anio')?.value;
    const ahora = new Date();
    if (anio === ahora.getFullYear()) {
      const mesActual = ahora.getMonth() + 1; // 1-based
      return this.meses.filter((m) => m.valor < mesActual);
    }
    return this.meses;
  }

  /** Al cambiar el año, corrige el mes si quedó inválido. */
  public onAnioChange(): void {
    const disponibles = this.mesesDisponibles;
    const mesActual = this.form?.get('mes')?.value;
    if (!disponibles.some((m) => m.valor === mesActual)) {
      const ultimo = disponibles.length
        ? disponibles[disponibles.length - 1].valor
        : null;
      this.form?.get('mes')?.setValue(ultimo);
    }
  }

  /** Sólo clientes con FacturacionCliente cargada y no bonificada. */
  private async cargarClientesFacturables(): Promise<void> {
    try {
      const listado = await this.facturacionService.getFiltered({
        filter: JSON.stringify({ bonificado: { $ne: true } }),
        populate: JSON.stringify({ path: 'cliente', select: 'nombre' }),
        limit: 0,
      });
      this.clientesFacturables = (listado.datos ?? [])
        .filter((f) => f.idCliente)
        .map((f) => ({
          idCliente: f.idCliente as string,
          nombre: f.cliente?.nombre ?? (f.idCliente as string),
        }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
    } catch (error) {
      console.error('Error cargando clientes facturables', error);
      this.helper.notifError('No se pudieron cargar los clientes');
    }
  }

  public async onSubmit(): Promise<void> {
    if (!this.form?.valid) {
      this.form?.markAllAsTouched();
      return;
    }
    this.loading = true;
    try {
      const { anio, mes, idCliente } = this.form.value;
      await this.gastosService.generarResumen(anio, mes, idCliente);
      this.helper.notifSuccess(
        'Generación iniciada. Se procesa en segundo plano; el resumen aparecerá al terminar.',
      );
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error generando resumen', error);
      this.helper.notifError('No se pudo generar el resumen');
    } finally {
      this.loading = false;
    }
  }
}
